RivalIQ.apps.AdsInsights.module('widgets', function (widgets, App, Backbone, Marionette, $, _) {

    'use strict';

    const PIE_CHART_SIZE = 130;
    const MAX_NUM_PIE_SLICES = 6;
    const OTHER_EPSILON = 0.01; // sometimes the children spend values don't add up exactly to the parent.

    function hasTooMuchDataError(metric) {
        return _.get(metric, ['data', 'error']) === 'TooMuchDataRequestedError';
    }

    function roundedFormattedValue(metric, abbreviate, options) {
        const formatter = RivalIQ.metric.format.expandedFormatFn(metric.attributes);
        const value = metric.getValue();

        if (RivalIQ.metric.property.type(metric.attributes) === RivalIQ.enums.dataType.CURRENCY) {
            let roundedFormattedValue = '';

            let roundedValue;
            if (value.value !== 0 && value.value < 1) {
                roundedFormattedValue = '< ';
                roundedValue = _.defaults({
                    value: 1
                }, value);
            } else {
                roundedValue = _.defaults({
                    value: Math.round(value.value)
                }, value);
            }

            const opts = _.extend({
                decimals: 0,
                abbreviate: abbreviate
            }, options);

            roundedFormattedValue += RivalIQ.formatter.formatAmountAndCurrency(roundedValue, opts);

            return roundedFormattedValue;
        } else {
            return formatter(value);
        }
    }

    function isPotentiallyMixedObjectives(dashboardModel) {
        return dashboardModel.getProp('adAccountId') === dashboardModel.getProp('adObjectId') && !dashboardModel.getProp('campaignObjective');
    }

    const PieChartSummaryItem = RivalIQ.apps.Dashboard.views.SummaryItem.mixin({

        template: '#adsWidgetMetricSummaryItemTemplate',
        tagName: 'div',

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        className: function () {
            const me = this;

            let classes = RivalIQ.apps.Dashboard.views.SummaryItem.prototype.className.call(me);

            classes += ' metric-summary-item';

            if (me.isClickable()) {
                classes += ' ad-clickable';
            }

            return classes;
        },

        events: {
            'click .title-col': 'onClick'
        },

        isClickable: function () {
            const me = this;

            const adObject = me.model.get('adObject');

            return adObject && !adObject.isBreakdown;
        },

        onClick: function () {
            const me = this;

            const adObject = me.model.get('adObject');

            if (me.isClickable()) {
                const params = _.extend(_.pick(me.dashboardModel.getProps(), App.utils.validCurationOptions), {
                    adAccountId: adObject.adAccountId,
                    adObjectId: adObject.id,
                    exploreLevel: App.utils.childExploreLevel(adObject.adObjectType)
                });

                const url = RivalIQ.apps.Dashboard.router.router.resolveCurationRoute(RivalIQ.enums.curation.FACEBOOK_ADS_INSIGHTS, params);
                RivalIQ.execute('history:navigate', url, {
                    trigger: true
                });
            }
        },

        serializeData: function () {
            const me = this;

            const formatter = RivalIQ.metric.format.expandedFormatFn(me.model.attributes);
            const formattedValue = formatter(me.model.getValue());

            return {
                formattedValue: formattedValue,
                roundedFormattedValue: roundedFormattedValue(me.model, false, {
                    currency: false
                }),
                title: me.model.getName()
            };
        },

        templateHelpers: function () {
            return RivalIQ.utils.emoji.getTemplateHelpers();
        }
    });

    const MetricObservationsGenerator = Marionette.Object.mixin({
        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        initialize: function () {
            const me = this;
            me.observationsGenerator = RivalIQ.apps.AdsInsights.data.ObservationsGenerator.createGenerator();
        },

        _generateRequestKey: function () {
            const me = this;

            const keyParts = [
                'adObjectId',
                'adAccountId',
                'campaignObjective',
                'campaignAdLabel',
                'campaignDeliveryStatus',
                'adsetDeliveryStatus',
                'boostedPostFilter',
                'tableMetrics',
                'orderBy',
                'sortOrder',
                'maxObservations',
                'breakdownName',
                'childObjectType',
                'timePeriod'
            ];

            const props = me.getProps();
            const key = _.reduce(keyParts, function (memo, keyPart) {
                return `${memo}-${JSON.stringify(props[keyPart])}`;
            }, '');

            return key;
        },

        generateObservations: function () {
            const me = this;

            const observationBaseMetric = RivalIQ.metric.build(me.getProps(), _.clone(me.dashboardModel.baseMetric));

            // This logic is to ensure that we have the same sort order here as in the table widget.
            const tableMetrics = RivalIQ.metric.build(observationBaseMetric, _.cloneDeep(me.getProp('tableMetrics')));
            const metricsCollection = new RivalIQ.collections.MetricDefinitions(tableMetrics);
            const sortColumns = metricsCollection.getColumnSortOrder(me.getProp('orderBy'));
            const baseMetric = RivalIQ.metric.build(observationBaseMetric, {
                orderBy: metricsCollection.getOrderBy(sortColumns)
            });

            // if we're already calculating observations for the same set of props, just return the existing deferred.
            const requestKey = me._generateRequestKey();
            if (requestKey !== me._lastRequestKey) {
                me._lastRequestKey = requestKey;
                me._deferred = me.observationsGenerator.generateObservations(baseMetric, me.dashboardModel.metrics, me.getProp('maxObservations'))
                    .then(function (observations) {
                        me.observations = observations;
                    });
            }

            return me._deferred;
        },

        findRelevantObservation: function (adObjectId) {
            const me = this;
            return _.find(me.observations, {
                rowId: adObjectId
            });
        }
    });

    const AdObjectsAnalysisView = RivalIQ.views.LayoutView.mixin({
        template: '#adObjectsAnalysisTemplate',
        childView: RivalIQ.apps.Dashboard.views.Widget,

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        dashboardModelEvents: {
            'change': 'reload'
        },

        regions: {
            analysisChart: '.x-analysis-chart',
            table: '.x-table',
            analysisChartLegend: '.x-analysis-chart-legend',
            observationsRegion: '.ads-observations'
        },

        ui: {
            analysisPropertyName: '.x-analysis-property-name',
            analysisLabel: '.x-analysis-label',
            analysisChart: '.x-analysis-section',
            analysisSection: '.ads-analysis',
            table: '.x-table',
            tooMuchDataMessage: '.x-too-much-data',
            noResultsMessage: '.x-no-results'
        },

        loadData: function () {
            const me = this;

            const analysisProperty = me.getProp('analysisProperty');
            const metrics = RivalIQ.metric.build(me.dashboardModel.pieChartMetric, [{
                property: 'adObject'
            }, {
                property: analysisProperty
            }, {
                property: 'results'
            }]);

            const parentMetricsBase = RivalIQ.metric.build(me.dashboardModel.pieChartMetric, {
                dimensions: ['channel', 'adObjectId', 'timeAnalysisMode'],
                breakdownName: undefined,
                childObjectType: undefined
            }, {
                clobberDimensions: true
            });

            const parentMetrics = RivalIQ.metric.build(parentMetricsBase, [{
                property: analysisProperty
            }, {
                property: 'impressions'
            }]);

            const metricsCollection = new RivalIQ.collections.MetricDefinitions(metrics);
            const parentMetricCollection = new RivalIQ.collections.MetricDefinitions(parentMetrics);

            return $.when(
                metricsCollection.fetch(),
                parentMetricCollection.fetch(),
                me.dashboardModel.metricObservationsGenerator.generateObservations()
            ).then(function (metrics, parentMetrics) {
                me.metrics = metrics;

                me.analysisPropertyTotal = parentMetrics.at(0);
                me.parentImpressions = parentMetrics.at(1);

                me.tooMuchData = metrics.any(hasTooMuchDataError);
            });
        },

        onLoadDataError: function (error) {
            const me = this;

            App.utils.handleLoadDataError(error, me.loadingView, me.dashboardModel);
        },

        onBeforeRenderLoaded: function () {
            const me = this;

            me.showTable();
        },

        onAttachLoaded: function () {
            const me = this;

            if (me.getProp('onlyShowTable')) {
                me.ui.analysisSection.remove();
                return;
            }

            if (!me.tooMuchData && !me.hadNoResults()) {
                me.showSummaries();
                me.showObservations();
            }
        },

        hadNoResults: function () {
            const me = this;
            return _.isEmpty(me.parentImpressions.getValue({
                format: false
            }));
        },

        onRenderLoaded: function () {
            const me = this;

            if (me.tooMuchData) {
                me.ui.analysisLabel.hide();
                me.ui.analysisChart.hide();
                me.ui.table.hide();
                me.ui.noResultsMessage.hide();
                me.ui.tooMuchDataMessage.show();
            } else {

                if (me.hadNoResults()) {
                    me.ui.analysisLabel.hide();
                    me.ui.analysisChart.hide();
                    me.ui.table.hide();
                    me.ui.noResultsMessage.show();
                    me.ui.tooMuchDataMessage.hide();
                } else {
                    me.ui.analysisLabel.show();
                    me.ui.analysisChart.show();
                    me.ui.table.show();
                    me.ui.noResultsMessage.hide();
                    me.ui.tooMuchDataMessage.hide();

                    me.dashboardModel.curation.triggerMethod('table:loaded', me);
                }
            }
        },

        showTable: function () {
            const me = this;

            // need to be strictly equal here, undefined is the default case. This is used for PPT export.
            if (me.getProp('onlyShowTable') === false) {
                return;
            }

            const widget = me.dashboardModel.getWidget('table');
            me.table.showWidget(widget, {
                className: `table-double-header ${me.dashboardModel.isExporting() ? 'x-table-container' : 'table-responsive'}`
            });

            me.listenTo(widget, 'change:sortOrder', function (model, sortOrder) {
                me.setProps('sortOrder', sortOrder, {
                    silent: false
                });
            });

            me.listenTo(widget, 'change:orderBy', function (model, orderBy) {
                me.setProps('orderBy', orderBy, {
                    silent: false
                });
            });
        },

        showObservations: function () {
            const me = this;

            const showObservations = !(me.dashboardModel.isExporting() && RivalIQ.state.account.get('hideInsightsOnExport'));
            if (showObservations) {
                const observationsView = new RivalIQ.apps.AdsInsights.views.ObservationsView({
                    showMixedCampaignWarning: isPotentiallyMixedObjectives(me),
                    observations: _.get(me, ['dashboardModel', 'metricObservationsGenerator', 'observations'], [])
                });

                me.observationsRegion.show(observationsView);
            }
        },

        getChart: function (metrics, data) {
            const view = new RivalIQ.graph.PieChart({
                height: PIE_CHART_SIZE,
                width: PIE_CHART_SIZE,
                dataProperty: 'data',
                data: data,
                concept: {
                    color: function (datum) {
                        return metrics.get(datum[0]).getColor();
                    },
                    getClassName: _.constant(null)
                }
            });

            return view;
        },

        getLegend: function (metrics) {
            const me = this;

            const legend = new RivalIQ.apps.Dashboard.views.SummaryItems({
                childView: PieChartSummaryItem,
                collection: metrics,
                tagName: 'div',
                childViewOptions: function () {
                    const summaryItemsMe = this;

                    const options = RivalIQ.apps.Dashboard.views.SummaryItems.prototype.childViewOptions.apply(summaryItemsMe, arguments);

                    return _.extend(options, {
                        dashboardModel: me.dashboardModel
                    });
                }
            });

            return legend;
        },

        hasLegend: _.constant(true),

        showSummaries: function () {
            const me = this;

            let data = _.sortBy(me.metrics.at(1).getValue({
                rawValue: true
            }), function (datum) {
                return -_.last(datum);
            });
            let accountedForValues = _.sumBy(data, _.last);
            const analysisPropertyTotal = me.analysisPropertyTotal.getValue({
                rawValue: true
            });

            if (accountedForValues < analysisPropertyTotal &&
                analysisPropertyTotal - accountedForValues > OTHER_EPSILON) {
                // we need an "other" bucket

                if (data.length === me.dashboardModel.pieChartMetric.limit) {
                    // we have to pull one off the end.
                    accountedForValues -= _.last(data)[1];
                    data = _.take(data, data.length - 1);
                }

                // it is possible that the breakdown buckets have a small amount of overlap (e.g., a user
                // views an ad from two devices during the time period). I think (and hope) those cases
                // are rare.
                data.push(['other', analysisPropertyTotal - accountedForValues]);
            }

            const adObjectsById = _.keyBy(me.metrics.at(0).getValue({
                format: false
            }), '0');
            const DataCollection = Backbone.Collection.extend({
                model: function (datum) {
                    const adObject = _.last(adObjectsById[datum[0]]);
                    let name = _.get(adObject, 'name');
                    if (!name && datum[0] === 'other') {
                        name = 'Other';
                    }

                    const valueTemplate = me.metrics.at(1).getValue({
                        format: false
                    })?.[0]?.[1];
                    let value = datum[1];

                    if (_.isObject(valueTemplate)) {
                        value = _.defaults({
                            value: value
                        }, valueTemplate);
                    }

                    const metric = me.metrics.at(1).clone({
                        value: value,
                        name: name,
                        colorIndex: data.indexOf(datum),
                        adObject: adObject
                    });

                    metric.cid = metric.id = datum[0];
                    return metric;
                }
            });

            const metrics = new DataCollection(data);

            if (me.hasLegend()) {
                const legend = me.getLegend(metrics);
                if (legend) {
                    me.analysisChartLegend.show(legend);
                }
            }

            let metricName = RivalIQ.metric.names.metricName(me.dashboardModel.pieChartMetric);
            // NOTE if the metric data type is one of currency then we should
            // try to get the abbreviated name of said currency to display in
            // `analysisPropertyName`
            if (RivalIQ.metric.property.type(me.dashboardModel.pieChartMetric) === RivalIQ.enums.dataType.CURRENCY) {
                // metric won't exist if data came back empty from FB.
                const metric = metrics.first();
                const currency = _.get(metric?.getValue(), ['currency']);
                if (currency) {
                    metricName = [metricName, currency].join(' in ');
                }
            }

            me.ui.analysisPropertyName.text(metricName);

            const chart = me.getChart(metrics, data);
            me.analysisChart.show(chart);
        },

        templateHelpers: function () {
            const me = this;
            return {
                hasLegend: me.hasLegend(),
                url: function () {
                    return me.dashboardModel.getPath({
                        widgetId: 'activity'
                    });
                }
            };
        }
    });

    AdObjectsAnalysisView.getMetricAttributes = function () {
        return {
            limit: MAX_NUM_PIE_SLICES
        };
    };

    const AgeGenderChartView = Marionette.LayoutView.extend({
        className: 'facebook-ads-age-gender',
        template: '#adsObjectsAnalysisAgeGenderChartTemplate',
        regions: {
            chart: '.chart-region'
        },
        onAttach: function () {
            const me = this;
            me.showChart();
        },
        showChart: function () {
            const me = this;
            const graph = me.getOption('graph');
            me.chart.show(graph);
        }
    });

    const AllAgeBuckets = [
        '13-17',
        '18-24',
        '25-34',
        '35-44',
        '45-54',
        '55-64',
        '65+'
    ];

    const AdObjectsAnalysisAgeGenderView = widgets._AdObjectsAnalysisAgeGenderView = AdObjectsAnalysisView.extend({
        getLegend: _.noop,
        hasLegend: _.constant(false),

        getChart: function (metrics, data) {
            const me = this;

            const genders = AdObjectsAnalysisAgeGenderView.normalizeAgeGenderData(data);
            const ageGenderData = _.map(genders, function (values, gender) {
                const genderData = _.map(values, function (value) {
                    let metric = metrics.get(_.head(value));

                    if (!metric) {
                        const template = metrics.at(0);
                        metric = template.clone({
                            value: _.defaults({
                                value: _.last(value)
                            }, template.get('value'))
                        });
                    }

                    const label = AdObjectsAnalysisAgeGenderView.getAgeRange(value);

                    return {
                        className: gender,
                        label: label,
                        value: _.last(value),
                        formatter: _.constant(roundedFormattedValue(metric, true, {
                            currency: false
                        }))
                    };
                });

                // NOTE we need to sort by our age ranges to make sure that the
                // data between genders is symmetric
                genderData.sort(me.sortByAgeGenderLabel);

                return genderData;
            });

            const distribution = new RivalIQ.graph.Distribution({
                data: ageGenderData,
                height: PIE_CHART_SIZE
            });

            return new AgeGenderChartView({
                graph: distribution
            });
        },

        sortByAgeGenderLabel: function (a, b) {
            const aLabelRangeStart = _.head(a.label.match(/(^\d+)/));
            const bLabelRangeStart = _.head(b.label.match(/(^\d+)/));
            return aLabelRangeStart - bLabelRangeStart;
        }
    });

    AdObjectsAnalysisAgeGenderView.getMetricAttributes = function () {
        // NOTE we need to remove the limit, and the filter flag if we want to
        // get all possible age/gender values (and we do for rendering the
        // distribution chart)
        return {
            limit: undefined,
            filterZeroSpend: undefined
        };
    };

    AdObjectsAnalysisAgeGenderView.getAgeRange = function (datum) {
        const demographic = _.head(datum);
        return demographic.substr(0, demographic.lastIndexOf('-'));
    };


    AdObjectsAnalysisAgeGenderView.normalizeAgeGenderData = function (data) {
        const groups = _.groupBy(data, function (datum) {
            const demographic = _.head(datum);
            const parts = demographic.split('-');
            return _.last(parts);
        });

        // NOTE the distribution graph only supports comparing two concepts; make sure we have male and female groups
        const genders = _.defaults(_.pick(groups, 'male', 'female'), {
            male: [],
            female: []
        });

        // make sure all the genders have the same set of age buckets.
        const allAgeBuckets = _(genders)
            .values()
            .flatten()
            .map(AdObjectsAnalysisAgeGenderView.getAgeRange)
            .union(AllAgeBuckets)
            .uniq()
            .value()
            .sort();

        _.each(genders, function (buckets, gender) {
            const sortedBuckets = _.sortBy(buckets, AdObjectsAnalysisAgeGenderView.getAgeRange);
            _.each(allAgeBuckets, function (age, idx) {
                if (!sortedBuckets[idx] || !AdObjectsAnalysisAgeGenderView.getAgeRange(sortedBuckets[idx]).startsWith(age)) {
                    sortedBuckets.splice(idx, 0, [`${age}-${gender}`, 0]);
                }
            });
            genders[gender] = sortedBuckets;
        });

        return genders;
    };

    const AdObjectsAnalysisPanel = RivalIQ.apps.Dashboard.views.Panel.extend({
        className: 'panel panel-default widget-insights-posts widget-ads-object-analysis'
    });

    const PanelFooter = RivalIQ.apps.Dashboard.views.RowCountPanelFooter.extend({
        template: '#adObjectsAnalysisPanelFooterTemplate',

        shouldShowRowCountSummary: function () {
            const me = this;

            return !me.tooMuchData;
        },

        loadData: function () {
            const me = this;

            const analysisProperty = me.getProp('analysisProperty');

            const table = me.dashboardModel.getWidget('table');

            const metric = RivalIQ.metric.build(me.dashboardModel.getProps(), _.defaults({
                orderBy: analysisProperty
            }, table.baseMetric));

            const freeDimension = _.head(RivalIQ.metric.getFreeDimensions(metric));
            metric.property = `${freeDimension}.total_count`;
            metric.dimensions = _.without(metric.dimensions, freeDimension);


            const rowsBeingShown = RivalIQ.metric.build(me.dashboardModel.getProps(), _.defaults({
                orderBy: analysisProperty,
                property: 'spend'
            }, table.baseMetric));

            const metricsCollection = new RivalIQ.collections.MetricDefinitions([metric, rowsBeingShown]);

            return metricsCollection.fetch()
                .done(function (metrics) {
                    me.tooMuchData = metrics.any(hasTooMuchDataError);
                    me.totalCount = metrics.at(0).getValue();
                    me.showingCount = _.size(metrics.at(1).getValue());
                });
        }
    });

    const AdObjectsAnalysisWidget = widgets._AdObjectsAnalysisWidget = RivalIQ.apps.Dashboard.models.Widget.mixin({
        panelView: AdObjectsAnalysisPanel,
        pngView: App.views.PngPanel,
        pdfView: App.views.PdfPanel,
        getExportSubtitle: App.utils.exportSubtitle,

        mixins: {
            exportHD: RivalIQ.apps.Dashboard.mixins.ExportHD
        },

        exportFormats: [
            RivalIQ.enums.exportFormat.PPT,
            RivalIQ.enums.exportFormat.PDF,
            RivalIQ.enums.exportFormat.PNG,
            RivalIQ.enums.exportFormat.CSV
        ],
        footerView: PanelFooter,
        hasFooter: _.constant(true),

        initialize: function () {
            const me = this;

            me.metricObservationsGenerator = new MetricObservationsGenerator({
                dashboardModel: me
            });

            RivalIQ.apps.Dashboard.models.Widget.prototype.initialize.apply(me);
        },

        getBreakdownName: function () {
            const me = this;
            return _.get(me, ['baseMetric', 'breakdownName']);
        },

        filter: function () {
            const me = this;

            const exploreLevel = me.getProp('exploreLevel');
            const breakdownName = me.getBreakdownName();
            if (!exploreLevel && !breakdownName) {
                return true;
            }
            return false;
        },

        title: function () {
            const me = this;

            const breakdownName = me.getBreakdownName();
            if (breakdownName) {
                return RivalIQ.strings[`facebook_ads_insights_${breakdownName}_title`];
            }

            return RivalIQ.strings[`facebook_ads_insights_${me.getProp('exploreLevel')}_title`];
        },

        description: function () {
            const me = this;

            const breakdownName = me.getBreakdownName();
            if (breakdownName) {
                return RivalIQ.str('facebook_ads_insights_ad_breakdowns_analysis_description', RivalIQ.strings[`facebook_ads_insights_${breakdownName}_title`]);
            }

            return RivalIQ.strings.facebook_ads_insights_ad_objects_analysis_description;

        },

        getView: function () {
            const me = this;

            const breakdownName = me.getBreakdownName();
            if (breakdownName === RivalIQ.enums.facebookAdStatsBreakdowns.AGE_GENDER.breakdownName) {
                return AdObjectsAnalysisAgeGenderView;
            }

            return AdObjectsAnalysisView;
        },

        processProps: function (props) {
            const me = this;

            _.defaults(props, {
                analysisProperty: 'spend',
                sortOrder: 1,
                orderBy: 1,
                limit: 4,
                maxObservations: 2
            });

            // We only want to show rows with 0 spend in details view. In the overview, we'll still
            // include 0 spend rows in the footer summaries but we won't show rows for them. See #12761
            if (props.limit > 0) {
                props.filterZeroSpend = true;
            }

            const analysisProperty = props.analysisProperty;

            const baseMetric = me.baseMetric;

            const view = me.getView();
            const viewMetricAttributes = _.result(view, 'getMetricAttributes');
            const pieChartMetricAttrs = _.defaults({}, viewMetricAttributes, {
                property: analysisProperty,
                orderBy: analysisProperty,
                sortOrder: 1
            }, baseMetric);
            me.pieChartMetric = RivalIQ.metric.build(props, pieChartMetricAttrs);

            return props;
        },

        widgetDefinitions: function () {
            const me = this;

            return [{
                instanceId: 'table',
                widgetId: RivalIQ.enums.widgetTypes.ADS_INSIGHTS_AD_OBJECT_TABLE,
                baseMetric: me.baseMetric,
                metricObservationsGenerator: me.metricObservationsGenerator,
                props: {
                    showDescriptions: true
                }
            }];
        },

        allowPageBreaks: function () {
            const me = this;

            const limit = me.getProp('limit');
            return limit === -1;
        },

        getSlides: function () {
            const me = this;

            return [
                me.buildSlide({
                    onlyShowTable: false
                }),
                me.buildSlide({
                    onlyShowTable: true
                })
            ];
        },

        viewMorePath: function () {
            const me = this;

            return me.getPath({
                limit: -1,
                orderBy: me.getProp('orderBy'),
                sortOrder: me.getProp('sortOrder')
            });
        },

        // This method starts the cache layer buffering on a prop change, and waits
        // for all the children to have been given a chance to `loadData` before halting
        // the buffering.
        proxyChangeEventsToChildren: function (changed, options) {
            const me = this;

            if (options.propagate === false) {
                return;
            }

            if (!_.isEmpty(changed) && !RivalIQ.data.CacheLayer.isBuffering()) {
                const widgets = me.getUnfilteredWidgets();

                if (widgets) {

                    RivalIQ.data.CacheLayer.startBuffering();

                    RivalIQ.apps.Dashboard.models.Widget.prototype.proxyChangeEventsToChildren.apply(me, arguments);

                    window.requestAnimationFrame(function () {
                        RivalIQ.data.CacheLayer.stopBuffering();
                        RivalIQ.data.CacheLayer.flush();
                    });
                }
            } else {
                RivalIQ.apps.Dashboard.models.Widget.prototype.proxyChangeEventsToChildren.apply(me, arguments);
            }
        },

        csvExportProps: function (props) {
            const me = this;

            return me.getWidget('table').csvExportProps(props);
        },

        getFilename: function () {
            const me = this;
            return App.utils.getExportFilename(me.getProps(), me.curation.adObject, me.getTitle());
        }
    });


    Marionette.Dashboard.widgets.add(RivalIQ.enums.widgetTypes.ADS_INSIGHTS_AD_OBJECTS_ANALYSIS, AdObjectsAnalysisWidget);

});
